import { useState, useEffect, useCallback } from "react";

const STATUS_COLORS = {
  kept: "#50c878", ordered: "#c4a265", bounced: "#e8593c",
  regenerated: "#7b68ee", paid: "#50c878", printing: "#c4a265",
  shipped: "#3b8bd4", delivered: "#50c878", failed: "#e8593c",
  completed: "#50c878", generating: "#c4a265"
};

const THEME_ACCENTS = {
  "Spirit of Ecstasy": "#c4a265", "Dark Obsidian": "#7b68ee",
  "Flowing Bronze": "#cd7f32", "Jade Dynasty": "#50c878",
  "Modern Chrome": "#c0c0c0", "Carved Walnut": "#deb887", "Unknown": "#888"
};

function Pill({ text, color }) {
  if (!text) return null;
  return (
    <span style={{
      display: "inline-block", padding: "3px 10px", borderRadius: 20,
      fontSize: 10, fontWeight: 600, letterSpacing: 0.8, textTransform: "uppercase",
      background: (color || "#555") + "18", color: color || "#888",
      border: "1px solid " + (color || "#555") + "30"
    }}>{text}</span>
  );
}

function Stars({ count }) {
  if (!count) return <span style={{ color: "#555", fontSize: 11 }}>—</span>;
  return (
    <span style={{ letterSpacing: 2 }}>
      {[1,2,3,4,5].map(i => (
        <span key={i} style={{ color: i <= count ? "#c4a265" : "#333", fontSize: 13 }}>
          {i <= count ? "\u2605" : "\u2606"}
        </span>
      ))}
    </span>
  );
}

function StatCard({ label, value, sub, accent }) {
  return (
    <div style={{
      flex: 1, minWidth: 140, padding: "20px 24px",
      background: "#0a0a0f", borderRadius: 12,
      border: "1px solid #1a1a2a", position: "relative", overflow: "hidden"
    }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: accent || "#c4a265" }}/>
      <div style={{ fontSize: 11, color: "#666", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 8, fontFamily: "monospace" }}>{label}</div>
      <div style={{ fontSize: 32, fontWeight: 300, color: "#e8e6e0", fontFamily: "Georgia, serif" }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "#555", marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function SculptureRow({ item, isExpanded, onToggle }) {
  const accent = THEME_ACCENTS[item.theme] || "#888";
  const timeAgo = getTimeAgo(item.session_started || item.created_at);

  return (
    <div style={{
      background: "#0a0a0f", borderRadius: 12, marginBottom: 8,
      border: isExpanded ? "1px solid " + accent + "40" : "1px solid #1a1a2a",
      transition: "border-color 0.3s", overflow: "hidden"
    }}>
      <div onClick={onToggle} style={{
        display: "flex", alignItems: "center", gap: 16, padding: "14px 20px",
        cursor: "pointer", userSelect: "none"
      }}>
        <div style={{ width: 8, height: 8, borderRadius: "50%", background: accent, flexShrink: 0 }}/>
        <div style={{ width: 52, fontSize: 10, color: "#666", fontFamily: "monospace", letterSpacing: 0.5, textTransform: "uppercase", flexShrink: 0 }}>
          {item.source || "—"}
        </div>
        <div style={{
          flex: 1, fontSize: 13, color: "#c8c6c0", lineHeight: 1.4,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"
        }}>
          {item.brief_text || "No brief"}
        </div>
        <div style={{ flexShrink: 0 }}>
          {item.feedback_action ? (
            <Pill text={item.feedback_action} color={STATUS_COLORS[item.feedback_action]} />
          ) : item.sculpture_status === "failed" ? (
            <Pill text="failed" color="#e8593c" />
          ) : (
            <Pill text={item.sculpture_status || "—"} color={STATUS_COLORS[item.sculpture_status] || "#555"} />
          )}
        </div>
        <div style={{ flexShrink: 0, width: 80, textAlign: "right" }}>
          <Stars count={item.feedback_rating} />
        </div>
        <div style={{ width: 60, fontSize: 10, color: "#555", textAlign: "right", fontFamily: "monospace", flexShrink: 0 }}>
          {timeAgo}
        </div>
        <div style={{
          color: "#444", fontSize: 12, transition: "transform 0.2s",
          transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)", flexShrink: 0
        }}>{"\u25B6"}</div>
      </div>

      {isExpanded && (
        <div style={{ padding: "0 20px 20px", borderTop: "1px solid #1a1a2a" }}>
          <div style={{ display: "flex", gap: 20, marginTop: 16 }}>
            <div style={{ width: 160, flexShrink: 0 }}>
              {item.flux_image_url ? (
                <img src={item.flux_image_url} alt="Sculpture" style={{
                  width: 160, height: 160, borderRadius: 8, objectFit: "cover",
                  border: "1px solid " + accent + "30"
                }}/>
              ) : (
                <div style={{
                  width: 160, height: 160, borderRadius: 8, background: "#111",
                  border: "1px dashed #333", display: "flex", alignItems: "center",
                  justifyContent: "center", fontSize: 11, color: "#444"
                }}>no image</div>
              )}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              {item.memory_extracted && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 10, color: "#666", letterSpacing: 1, textTransform: "uppercase", marginBottom: 4, fontFamily: "monospace" }}>memory core</div>
                  <div style={{ fontSize: 13, color: "#c4a265", fontStyle: "italic", lineHeight: 1.5 }}>"{item.memory_extracted}"</div>
                </div>
              )}
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 10, color: "#666", letterSpacing: 1, textTransform: "uppercase", marginBottom: 4, fontFamily: "monospace" }}>brief</div>
                <div style={{ fontSize: 12, color: "#999", lineHeight: 1.6 }}>{item.brief_text || "—"}</div>
              </div>
              <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                <MetaItem label="theme" value={item.theme || "—"} color={accent} />
                <MetaItem label="gen time" value={item.generation_time_ms ? (item.generation_time_ms/1000).toFixed(1) + "s" : "—"} />
                <MetaItem label="view time" value={item.view_duration_seconds ? item.view_duration_seconds + "s" : "—"} />
                <MetaItem label="email" value={item.user_email || "anonymous"} />
                <MetaItem label="source" value={item.source || "—"} />
                {item.glb_url && <MetaItem label="3D model" value="saved" color="#50c878" />}
                {item.order_id && (
                  <>
                    <MetaItem label="tier" value={item.tier} color={item.tier === "metal" ? "#c4a265" : "#7b68ee"} />
                    <MetaItem label="price" value={"$" + (item.price_cents/100).toFixed(0)} color="#50c878" />
                    <MetaItem label="order" value={item.order_status} color={STATUS_COLORS[item.order_status]} />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MetaItem({ label, value, color }) {
  return (
    <div>
      <div style={{ fontSize: 9, color: "#555", letterSpacing: 1, textTransform: "uppercase", marginBottom: 2, fontFamily: "monospace" }}>{label}</div>
      <div style={{ fontSize: 12, color: color || "#888" }}>{value}</div>
    </div>
  );
}

function getTimeAgo(dateStr) {
  if (!dateStr) return "—";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return mins + "m ago";
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return hrs + "h ago";
  return Math.floor(hrs / 24) + "d ago";
}

export default function Admin() {
  const [pipeline, setPipeline] = useState([]);
  const [gold, setGold] = useState([]);
  const [stats, setStats] = useState({ sessions: 0, sculptures: 0, orders: 0, revenue: 0 });
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState("all");
  const [view, setView] = useState("pipeline");

  const fetchData = useCallback(async () => {
    try {
      const [pRes, sRes, gRes] = await Promise.all([
        fetch("/api/db?type=pipeline"),
        fetch("/api/db?type=stats"),
        fetch("/api/db?type=gold"),
      ]);
      const pData = await pRes.json();
      const sData = await sRes.json();
      const gData = await gRes.json();
      setPipeline(pData.data || []);
      setStats(sData);
      setGold(gData.data || []);
    } catch (e) {
      console.error("Failed to fetch admin data:", e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = (view === "gold" ? gold : pipeline).filter(item => {
    if (filter === "all") return true;
    if (filter === "ordered") return item.feedback_action === "ordered";
    if (filter === "kept") return item.feedback_action === "kept";
    if (filter === "bounced") return item.feedback_action === "bounced" || item.sculpture_status === "failed";
    if (filter === "gold") return item.feedback_rating >= 4;
    if (filter === "studio") return item.source === "studio";
    if (filter === "chat") return item.source === "chat";
    return true;
  });

  const convRate = stats.sessions > 0 ? ((stats.orders / stats.sessions) * 100).toFixed(1) : "0";

  if (loading) return (
    <div style={{ background: "#06060a", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: "#555", fontFamily: "Georgia, serif", fontSize: 16, fontStyle: "italic" }}>
      Loading your sculptures...
    </div>
  );

  return (
    <div style={{ background: "#06060a", color: "#e8e6e0", minHeight: "100vh", fontFamily: "-apple-system, sans-serif", padding: "24px 32px" }}>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
        * { box-sizing: border-box; margin: 0; padding: 0; }
      `}</style>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 32 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 32, height: 32, borderRadius: "50%", border: "1.5px solid #c4a265",
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, color: "#c4a265"
          }}>{"\u2661"}</div>
          <span style={{ fontSize: 22, fontFamily: "Georgia, serif", fontWeight: 300, fontStyle: "italic", color: "#e8e6e0" }}>Yours Truly</span>
          <span style={{ fontSize: 10, color: "#555", letterSpacing: 2, textTransform: "uppercase", fontFamily: "monospace", marginLeft: 8 }}>admin</span>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {["pipeline", "gold briefs"].map(v => (
            <button key={v} onClick={() => setView(v === "gold briefs" ? "gold" : v)} style={{
              padding: "6px 16px", borderRadius: 20, fontSize: 11, cursor: "pointer",
              letterSpacing: 0.8, textTransform: "uppercase", border: "none", fontFamily: "monospace",
              background: (v === "gold briefs" ? "gold" : v) === view ? "#c4a26520" : "transparent",
              color: (v === "gold briefs" ? "gold" : v) === view ? "#c4a265" : "#555"
            }}>{v}</button>
          ))}
          <button onClick={fetchData} style={{
            padding: "6px 16px", borderRadius: 20, fontSize: 11, cursor: "pointer",
            letterSpacing: 0.8, border: "1px solid #1a1a2a", background: "transparent",
            color: "#666", fontFamily: "monospace"
          }}>refresh</button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
        <StatCard label="Sessions" value={stats.sessions} accent="#7b68ee" />
        <StatCard label="Sculptures" value={stats.sculptures} accent="#c4a265" />
        <StatCard label="Orders" value={stats.orders} sub={stats.revenue > 0 ? "$" + (stats.revenue/100).toFixed(0) + " revenue" : "no orders yet"} accent="#50c878" />
        <StatCard label="Conversion" value={convRate + "%"} sub={gold.length + " gold briefs"} accent="#e8593c" />
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {[
          { key: "all", label: "All" },
          { key: "chat", label: "Chat" },
          { key: "studio", label: "Studio" },
          { key: "ordered", label: "Ordered" },
          { key: "bounced", label: "Bounced" },
          { key: "gold", label: "\u2605 Gold" }
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} style={{
            padding: "5px 14px", borderRadius: 16, fontSize: 11, cursor: "pointer",
            border: filter === f.key ? "1px solid #c4a26550" : "1px solid #1a1a2a",
            background: filter === f.key ? "#c4a26512" : "transparent",
            color: filter === f.key ? "#c4a265" : "#666", fontFamily: "-apple-system, sans-serif"
          }}>{f.label}</button>
        ))}
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 11, color: "#444", alignSelf: "center", fontFamily: "monospace" }}>{filtered.length} results</span>
      </div>

      {/* Column headers */}
      <div style={{
        display: "flex", alignItems: "center", gap: 16, padding: "8px 20px",
        fontSize: 9, color: "#444", letterSpacing: 1.5, textTransform: "uppercase", fontFamily: "monospace"
      }}>
        <div style={{ width: 8 }}/>
        <div style={{ width: 52 }}>src</div>
        <div style={{ flex: 1 }}>brief</div>
        <div style={{ width: 90 }}>status</div>
        <div style={{ width: 80, textAlign: "right" }}>rating</div>
        <div style={{ width: 60, textAlign: "right" }}>when</div>
        <div style={{ width: 12 }}/>
      </div>

      {/* Rows */}
      <div>
        {filtered.map((item, idx) => (
          <SculptureRow
            key={item.sculpture_id || item.session_id || idx}
            item={item}
            isExpanded={expandedId === (item.sculpture_id || idx)}
            onToggle={() => setExpandedId(expandedId === (item.sculpture_id || idx) ? null : (item.sculpture_id || idx))}
          />
        ))}
      </div>

      {filtered.length === 0 && (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#444", fontSize: 13 }}>
          {loading ? "Loading..." : "No sculptures match this filter"}
        </div>
      )}

      {/* Footer */}
      <div style={{
        marginTop: 32, padding: "16px 0", borderTop: "1px solid #1a1a2a",
        fontSize: 10, color: "#333", fontFamily: "monospace",
        display: "flex", justifyContent: "space-between"
      }}>
        <span>yourstruly.gift/admin</span>
        <span>Live data from Supabase</span>
      </div>
    </div>
  );
}
